import { type ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, Checkbox, Input, Modal, Select, Space, Spin, Typography } from '@/shared/ui/primitives';
import { useTranslation } from 'react-i18next';
import FilterPanel from '@/shared/components/FilterPanel';
import { DeleteOutlined, PlusOutlined, StarOutlined } from '@/shared/ui/icons';
import DynamicFilterRow from './DynamicFilterRow';
import { emptyFilters } from './filterSerialization';
import {
  createAdminFilterPreset,
  deleteAdminFilterPreset,
  listAdminFilterPresets,
  setDefaultAdminFilterPreset,
  updateAdminFilterPreset,
} from './api';
import type { AdminFilterResource, DynamicFilterCondition, DynamicFilterGroup, DynamicRelationLookups, FilterMetadataResponse } from './types';

type Props = {
  resource?: AdminFilterResource;
  metadata?: FilterMetadataResponse;
  loading?: boolean;
  value: DynamicFilterGroup;
  relationLookups?: DynamicRelationLookups;
  onChange: (value: DynamicFilterGroup) => void;
  onReset: () => void;
};

export default function DynamicFilterBuilder({ resource, metadata, loading, value, relationLookups, onChange, onReset }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedPresetId, setSelectedPresetId] = useState<number | undefined>();
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const fields = metadata?.fields ?? [];
  const maxConditions = metadata?.limits?.maxConditions ?? 20;
  const canAdd = fields.length > 0 && value.conditions.length < maxConditions;
  const presetQuery = useQuery({
    queryKey: ['admin-filter-presets', resource],
    queryFn: () => listAdminFilterPresets(resource as AdminFilterResource),
    enabled: Boolean(resource),
    staleTime: 30 * 1000,
  });
  const presets = presetQuery.data ?? [];
  const selectedPreset = presets.find((preset) => preset.id === selectedPresetId);
  const hasFilters = value.conditions.length > 0;

  const presetOptions = useMemo(() => presets.map((preset) => ({
    value: preset.id,
    label: preset.isDefault ? `${preset.name} (${t('dynamicFilters.presets.default')})` : preset.name,
  })), [presets, t]);

  useEffect(() => {
    if (!selectedPresetId) return;
    if (!presets.some((preset) => preset.id === selectedPresetId)) {
      setSelectedPresetId(undefined);
    }
  }, [presets, selectedPresetId]);

  useEffect(() => {
    if (selectedPresetId || presets.length === 0) return;
    const current = JSON.stringify(value);
    const matchingPreset = presets.find((preset) => JSON.stringify(preset.filters) === current);
    if (matchingPreset) {
      setSelectedPresetId(matchingPreset.id);
    }
  }, [presets, selectedPresetId, value]);

  const invalidatePresets = () => {
    if (!resource) return;
    queryClient.invalidateQueries({ queryKey: ['admin-filter-presets', resource] });
  };

  const createMutation = useMutation({
    mutationFn: () => createAdminFilterPreset(resource as AdminFilterResource, {
      name: presetName,
      filters: value,
      isDefault: saveAsDefault,
    }),
    onSuccess: (preset) => {
      setSelectedPresetId(preset.id);
      setSaveOpen(false);
      setPresetName('');
      setSaveAsDefault(false);
      invalidatePresets();
      toast.success(t('dynamicFilters.presets.saved'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('dynamicFilters.presets.saveFailed')),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateAdminFilterPreset(resource as AdminFilterResource, selectedPresetId as number, {
      filters: value,
    }),
    onSuccess: () => {
      invalidatePresets();
      toast.success(t('dynamicFilters.presets.updated'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('dynamicFilters.presets.updateFailed')),
  });

  const defaultMutation = useMutation({
    mutationFn: () => setDefaultAdminFilterPreset(resource as AdminFilterResource, selectedPresetId as number),
    onSuccess: () => {
      invalidatePresets();
      toast.success(t('dynamicFilters.presets.defaultSaved'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('dynamicFilters.presets.defaultFailed')),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAdminFilterPreset(resource as AdminFilterResource, selectedPresetId as number),
    onSuccess: () => {
      setSelectedPresetId(undefined);
      invalidatePresets();
      toast.success(t('dynamicFilters.presets.deleted'));
    },
    onError: (err: any) => toast.error(err.response?.data?.message || t('dynamicFilters.presets.deleteFailed')),
  });

  const openSaveModal = () => {
    setPresetName('');
    setSaveAsDefault(false);
    setSaveOpen(true);
  };

  const savePreset = () => {
    if (!presetName.trim()) {
      toast.error(t('dynamicFilters.presets.nameRequired'));
      return;
    }
    createMutation.mutate();
  };

  const addCondition = () => {
    if (!canAdd) return;
    const field = fields[0];
    const condition: DynamicFilterCondition = {
      field: field.key,
      operator: field.operators[0],
      value: field.operators[0] === 'BETWEEN' ? { from: '', to: '' } : '',
    };
    onChange({ logic: 'AND', conditions: [...value.conditions, condition] });
  };

  const updateCondition = (index: number, condition: DynamicFilterCondition) => {
    const next = value.conditions.slice();
    next[index] = condition;
    onChange({ logic: 'AND', conditions: next });
  };

  const removeCondition = (index: number) => {
    onChange({ logic: 'AND', conditions: value.conditions.filter((_, itemIndex) => itemIndex !== index) });
  };

  return (
    <FilterPanel onReset={() => { onChange(emptyFilters()); onReset(); }} defaultOpen={value.conditions.length > 0}>
      {loading && <Spin />}
      {!loading && fields.length === 0 && (
        <Typography.Text type="secondary">{t('dynamicFilters.empty')}</Typography.Text>
      )}
      {fields.length > 0 && (
        <>
          {resource && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <Select
                allowClear
                showSearch
                loading={presetQuery.isLoading}
                value={selectedPresetId}
                options={presetOptions}
                placeholder={t('dynamicFilters.presets.select')}
                style={{ minWidth: 220, flex: '1 1 260px', maxWidth: 420 }}
                onChange={(id) => {
                  setSelectedPresetId(id);
                  const preset = presets.find((item) => item.id === id);
                  if (preset) onChange(preset.filters);
                }}
                onClear={() => setSelectedPresetId(undefined)}
              />
              <Space wrap>
                <Button onClick={openSaveModal} disabled={!hasFilters || createMutation.isPending}>
                  {t('dynamicFilters.presets.saveAs')}
                </Button>
                <Button
                  onClick={() => updateMutation.mutate()}
                  disabled={!selectedPreset || !hasFilters || updateMutation.isPending}
                >
                  {t('dynamicFilters.presets.updateSelected')}
                </Button>
                <Button
                  icon={<StarOutlined />}
                  onClick={() => defaultMutation.mutate()}
                  disabled={!selectedPreset || selectedPreset.isDefault || defaultMutation.isPending}
                >
                  {t('dynamicFilters.presets.makeDefault')}
                </Button>
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm(t('dynamicFilters.presets.deleteConfirm'))) {
                      deleteMutation.mutate();
                    }
                  }}
                  disabled={!selectedPreset || deleteMutation.isPending}
                >
                  {t('common.delete')}
                </Button>
              </Space>
            </div>
          )}
          {value.conditions.map((condition, index) => (
            <DynamicFilterRow
              key={`${condition.field}-${index}`}
              fields={fields}
              condition={condition}
              relationLookups={relationLookups}
              onChange={(next) => updateCondition(index, next)}
              onRemove={() => removeCondition(index)}
            />
          ))}
          <Button icon={<PlusOutlined />} onClick={addCondition} disabled={!canAdd}>
            {t('dynamicFilters.addFilter')}
          </Button>
          <Modal
            open={saveOpen}
            title={t('dynamicFilters.presets.saveTitle')}
            okText={t('common.save')}
            cancelText={t('common.cancel')}
            confirmLoading={createMutation.isPending}
            onOk={savePreset}
            onCancel={() => setSaveOpen(false)}
          >
            <div style={{ display: 'grid', gap: 12 }}>
              <Input
                autoFocus
                value={presetName}
                placeholder={t('dynamicFilters.presets.namePlaceholder')}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setPresetName(event.target.value)}
                onPressEnter={savePreset}
              />
              <Checkbox checked={saveAsDefault} onChange={(event) => setSaveAsDefault(event.target.checked)}>
                {t('dynamicFilters.presets.saveAsDefault')}
              </Checkbox>
            </div>
          </Modal>
        </>
      )}
    </FilterPanel>
  );
}
